import { last, size, keys, partition } from "lodash";

import * as actions from "./actions";

import {
  ANIMATION_DURATION,
  DependencyType,
  QuestionKind,
  ScreenType,
  SurveyMedium,
} from "consts";
import { getSurveyQuestionnaire } from "state/modules/survey/selectors";
import { getSelectedOptions } from "state/modules/response/selectors";
import { submitFeedback } from "state/modules/feedback";
import {
  discardLastResponseTimer,
  resetLastResponseTimer,
  validateCurrentSection,
  clearSection,
  _addOption,
  _addSubOption,
  _clearQuestion,
  _setOptionOtherText,
  _setSubOptionOtherText,
} from "state/modules/response";
import {
  setSurveyStatus,
  setSurveyMessage,
  STATUS,
  setQuizScore,
  discardSurvey,
  startQuestionnaire,
} from "state/modules/survey";

import { Navigator } from "router";

//                            HARD CODED QUESTIONS                            \\
// ========================================================================== \\

const getDrilledDownSection = currentSection => (dispatch, getState) => {
  const state = getState();
  const drilledDownQuestions = currentSection.questions
    .map(question => {
      if (question.app_screen_type !== ScreenType.DRILLED_DOWN) {
        const questionResponse =
          state.response.questions[question.virtual_id] || {};
        const selectedOptionIds =
          keys(questionResponse.options).map(oId => parseInt(oId, 10)) || [];

        if (size(selectedOptionIds)) {
          const drilledDownOptions = selectedOptionIds
            .map(optionId =>
              question.options.find(o => o.virtual_id === optionId),
            )
            .filter(option => option?.sub_options?.length);

          if (size(drilledDownOptions)) {
            return getDrilledDownQuestion(question, drilledDownOptions);
          }
        }
      }
    })
    .filter(q => q);

  if (drilledDownQuestions.length) {
    const _index = state.navigation.currentSectionIndex + 1;
    dispatch(actions.setCurrentSectionIndex(_index));
    dispatch(actions.adjustDrilledDownCountBy(1));
    const drilledDownSection = {
      ...currentSection,
      questions: drilledDownQuestions,
      _index,
    };
    return drilledDownSection;
  }
};

const getDrilledDownQuestion = (currentQuestion, options) => {
  return {
    ...currentQuestion,
    is_statement_visible: true,
    options,
    app_screen_type: ScreenType.DRILLED_DOWN,
    kind: ScreenType.DRILLED_DOWN,
  };
};

const getDivisionQuestion = () => (_, getState) => {
  const state = getState();
  if (state.survey.divisionInfo) {
    return {
      is_statement_visible: false,
      app_screen_type: ScreenType.DIVISION,
    };
  }
};

//                             NAVIGATON INTERFACE                            \\
// ========================================================================== \\

export const startSurvey = () => dispatch => {
  dispatch(startQuestionnaire());
  setTimeout(() => Navigator.jumpTo("survey"), 100); // wait for questionnaire to be selected
  dispatch(setStartingSection());
};

export const navForward = () => (dispatch, getState) => {
  dispatch(resetLastResponseTimer());

  const state = getState();
  const currentSection = last(state.navigation.sectionStack);

  if (dispatch(wasFinishOptionSelected(currentSection))) {
    dispatch(onQuestionnaireEnd());
    return;
  }

  const drilledDownSection = dispatch(getDrilledDownSection(currentSection));
  if (drilledDownSection) {
    dispatch(setCurrentSection(drilledDownSection));
    return;
  }

  const nextSection = dispatch(selectNextSection());
  if (!nextSection) {
    dispatch(onQuestionnaireEnd());
    return;
  }

  const shouldAutoSelect = canAutoSelect(nextSection);
  if (shouldAutoSelect) {
    const driledDownSectionForAutoSelected = dispatch(
      autoSelectSection(nextSection, currentSection),
    );
    driledDownSectionForAutoSelected
      ? dispatch(setCurrentSection(driledDownSectionForAutoSelected))
      : dispatch(navForward());
    return;
  }

  shuffleQuestionOptions(nextSection);
  dispatch(setCurrentSection(nextSection));
};

export const navBackward = () => (dispatch, getState) => {
  const state = getState();
  const { sectionStack } = state.navigation;
  const section = last(sectionStack);
  dispatch(clearSection(section));
  dispatch(removeCurrentSection(section));
  if (sectionStack.length === 1) {
    dispatch(setSurveyStatus(STATUS.PENDING));
    dispatch(discardSurvey());
    Navigator.jumpTo("start");
    dispatch(discardLastResponseTimer());
    return;
  }
  const { sectionStack: updatedStack } = getState().navigation;
  const previousSection = last(updatedStack);
  dispatch(actions.setCurrentSectionIndex(previousSection._index));
  dispatch(clearPreviousAutoSelected(previousSection));
  dispatch(validateCurrentSection(previousSection));
  dispatch(resetLastResponseTimer());
};

const removeCurrentSection = section => dispatch => {
  const isDrilledDown = section.questions.some(
    question => question.app_screen_type === ScreenType.DRILLED_DOWN,
  );
  isDrilledDown && dispatch(actions.adjustDrilledDownCountBy(-1));
  dispatch(actions.removeCurrentSection());
};

const clearPreviousAutoSelected = section => (dispatch, getState) => {
  const state = getState();
  const autoSelectedQuestionId = state.navigation.autoSelected[section._index];
  if (autoSelectedQuestionId) {
    dispatch(_clearQuestion(autoSelectedQuestionId));
  }
};

//                             INTERNAL FUNCTIONS                             \\
// ========================================================================== \\

const setStartingSection = () => dispatch => {
  const section = dispatch(selectNextSection());
  if (!section) {
    dispatch(setSurveyStatus(STATUS.EXPIRED));
    dispatch(setSurveyMessage("You have already submitted this survey."));
    return;
  }

  shuffleQuestionOptions(section);

  const divisionQuestion = dispatch(getDivisionQuestion());
  if (divisionQuestion) {
    section.questions = [divisionQuestion, ...section.questions];
  }
  dispatch(setCurrentSection(section));
};

const setCurrentSection = section => dispatch => {
  section.questions.forEach(question => {
    question.startTime = new Date().getTime() + ANIMATION_DURATION;
  });
  dispatch(actions.setCurrentSection(section));
  dispatch(validateCurrentSection(section));
};

const selectNextSection = () => (dispatch, getState) => {
  const state = getState();
  const { questionnaire_sections } = getSurveyQuestionnaire(state);
  const { currentSectionIndex, drilledDownCount } = state.navigation;

  let candidateSection;
  let isCandidateEligible;
  let candidateIndex = currentSectionIndex - drilledDownCount;

  while (!isCandidateEligible) {
    candidateIndex++;
    if (candidateIndex >= questionnaire_sections.length) {
      return;
    }
    candidateSection = deepCopySection(questionnaire_sections[candidateIndex]);
    // reset to _index on backward navigation
    candidateSection._index = candidateIndex + drilledDownCount;
    isCandidateEligible = dispatch(canSectionRender(candidateSection));
  }

  dispatch(actions.setCurrentSectionIndex(candidateIndex + drilledDownCount));
  return candidateSection;
};

const onQuestionnaireEnd = () => (dispatch, getState) => {
  const state = getState();
  if (!state.navigation.is_submit_clicked) {
    state.survey.isQuiz
      ? dispatch(viewQuizResult())
      : dispatch(submitFeedback());
  }
  dispatch(actions.setIsSubmitClicked(true));
};

const deepCopySection = stateSection => {
  // checkers and actions can mutate candidate question
  // create deep copy to avoid redux state mutation
  return {
    id: stateSection.id,
    title: stateSection.title,
    title_urdu: stateSection.title_urdu,
    questions: stateSection.questions.map(stateQuestion => {
      return {
        ...stateQuestion,
        options: stateQuestion.options.map(option => {
          return {
            ...option,
          };
        }),
      };
    }),
  };
};

const isQuestionSelected = (dependency, response) =>
  !!response?.questions?.[dependency.question];

const isOptionSelected = (dependency, response) =>
  !!response?.questions?.[dependency.question]?.options[dependency.option];

const isSubOptionSelected = (dependency, response) =>
  !!response?.questions?.[dependency.question]?.options[dependency.option]
    ?.subOptions[dependency.sub_option];

const checkResponseDependency = checker => dependency => (_, getState) => {
  const { response, survey } = getState();
  return [response, survey.previousStagesResponse].some(resp =>
    checker(dependency, resp),
  );
};

const divisionDependency = dependency => (_, getState) => {
  return dependency.division === getState().division.id;
};

const customerDependency = dependency => (_, getState) => {
  const field = getState().customer[dependency.field];
  return typeof field === "string" ? !!field.length : !!field;
};

const mediumDependency = dependency => (_, getState) => {
  const state = getState();
  switch (dependency.medium) {
    case SurveyMedium.WEB:
      return !!state.survey.inviteId;
    case SurveyMedium.APP:
      return !!state.taker.id;
    default:
      return false;
  }
};

const stageDependency = dependency => (_, getState) => {
  const { survey, customer } = getState();
  if (!survey.stage) {
    return true;
  }
  return customer.id
    ? survey.stage === dependency.stage
    : dependency.stage === 1;
};

const dependencyChecker = {
  [DependencyType.QUESTION]: checkResponseDependency(isQuestionSelected),
  [DependencyType.OPTION]: checkResponseDependency(isOptionSelected),
  [DependencyType.SUB_OPTION]: checkResponseDependency(isSubOptionSelected),
  [DependencyType.DIVISION]: divisionDependency,
  [DependencyType.CUSTOMER]: customerDependency,
  [DependencyType.MEDIUM]: mediumDependency,
  [DependencyType.STAGE]: stageDependency,
};

const evaluateDependency = dependency => dispatch => {
  const checker = dependencyChecker[dependency.type];
  if (checker) {
    return dispatch(checker(dependency));
  }
};

const canRender = item => dispatch => {
  const [stageConditions, conditionsA] = partition(
    item.depends_upon,
    d => d.type === DependencyType.STAGE,
  );

  const [hideStageConditions, showStageConditions] = partition(
    stageConditions,
    d => d.disable,
  );

  if (hideStageConditions.some(d => dispatch(evaluateDependency(d)))) {
    return false;
  }

  if (
    showStageConditions.length &&
    !showStageConditions.some(d => dispatch(evaluateDependency(d)))
  ) {
    return false;
  }

  const [mediumConditions, conditionsB] = partition(
    conditionsA,
    d => d.type === DependencyType.MEDIUM,
  );

  const isCorrectMedium = mediumConditions.some(d =>
    dispatch(evaluateDependency(d)),
  );

  if (mediumConditions.length && !isCorrectMedium) {
    return false;
  }

  const [hideConditions, showConditions] = partition(
    conditionsB,
    d => d.disable,
  );

  const shouldHide = hideConditions.some(d => dispatch(evaluateDependency(d)));
  if (shouldHide) {
    return false;
  }

  const shouldShow = showConditions.some(d => dispatch(evaluateDependency(d)));
  return !showConditions.length || shouldShow;
};

const canSectionRender = section => dispatch => {
  section.questions = section.questions.filter(question => {
    return dispatch(canQuestionRender(question));
  });

  return !!section.questions.length;
};

const canQuestionRender = question => dispatch => {
  return (
    dispatch(canRender(question)) &&
    dispatch(canOptionsRender(question)) &&
    !dispatch(isQuestionAlreadyAnswered(question))
  );
};

const canOptionsRender = question => dispatch => {
  if (
    !!question.options &&
    (question.kind === QuestionKind.DESCRIPTION ||
      question.kind === QuestionKind.ASSIGNEE)
  ) {
    return true;
  }
  question.options = question.options.filter(option => {
    if (!dispatch(canRender(option))) {
      return false;
    }
    option.sub_options = option.sub_options?.filter(subOption =>
      dispatch(canRender(subOption)),
    );
    return true;
  });
  return !!question.options.length;
};

const autoSelectWhiteList = [
  ScreenType.CHECK_BOX,
  ScreenType.IMAGE,
  ScreenType.ICON,
];

const canAutoSelect = section => {
  if (section.questions.length > 1) {
    return false;
  }
  const { app_screen_type, is_required, options } = section.questions[0];
  return (
    is_required &&
    options.length === 1 &&
    !options[0].is_other &&
    autoSelectWhiteList.includes(app_screen_type)
  );
};

const autoSelectSection = (section, resetSection) => dispatch => {
  const question = section.questions[0];
  const option = question.options[0];
  const telemetry = createDuplicateTelemetryData(1000);
  dispatch(
    _addOption(question.virtual_id, option.virtual_id, option.id, telemetry),
  );
  dispatch(actions.storeAutoSelected(resetSection._index, question.virtual_id));
  return dispatch(getDrilledDownSection(section));
};

const shuffleQuestionOptions = section => {
  section.questions.forEach(question => {
    question.options = shuffleOptions(question.options);
    question.options.forEach(option => {
      if (option.sub_options) {
        option.sub_options = shuffleOptions(option.sub_options);
      }
    });
  });
};

const shuffleOptions = options => {
  const randomizables = options.filter(o => o.is_randomizable);
  return options.map(option => {
    if (!option.is_randomizable) {
      return option;
    }
    const randomIndex = Math.floor(Math.random() * randomizables.length);
    const [random] = randomizables.splice(randomIndex, 1);
    return random;
  });
};

export const viewQuizResult = () => dispatch => {
  dispatch(setQuizScore());
  Navigator.jumpTo("result");
  dispatch(setSurveyStatus(STATUS.RESULT));
};

const isQuestionAlreadyAnswered = question => (dispatch, getState) => {
  // previousResponse contain actual ids whereas current response uses virtual_ids for

  const state = getState();
  const { previousResponse } = state.survey;

  const answeredQuestion = previousResponse.questions?.[question.virtual_id];
  if (!answeredQuestion) {
    return false;
  }

  const answeredOptionIds = keys(answeredQuestion.options).map(id =>
    parseInt(id, 10),
  );
  const options = question.options.filter(option => {
    return answeredOptionIds.includes(option.virtual_id);
  });

  options.forEach(option => {
    const answeredOption = answeredQuestion.options[option.virtual_id];

    const { other_text, duration } = answeredOption;
    const telemetry = createDuplicateTelemetryData(duration);

    other_text
      ? dispatch(
          _setOptionOtherText(
            question.virtual_id,
            option.virtual_id,
            option.id,
            other_text,
            telemetry,
          ),
        )
      : dispatch(
          _addOption(
            question.virtual_id,
            option.virtual_id,
            option.id,
            telemetry,
          ),
        );

    const answeredSubOptionIds = keys(answeredOption.subOptions).map(id =>
      parseInt(id, 10),
    );

    const subOptions =
      option.sub_options?.filter(subOption =>
        answeredSubOptionIds.includes(subOption.virtual_id),
      ) || [];

    subOptions.forEach(subOption => {
      const answeredSubOption = answeredOption.subOptions[subOption.virtual_id];

      const { other_text, duration } = answeredSubOption;
      const telemetry = createDuplicateTelemetryData(duration);

      other_text
        ? dispatch(
            _setSubOptionOtherText(
              question.virtual_id,
              option.virtual_id,
              subOption.virtual_id,
              subOption.id,
              other_text,
              telemetry,
            ),
          )
        : dispatch(
            _addSubOption(
              question.virtual_id,
              option.virtual_id,
              subOption.virtual_id,
              subOption.id,
              telemetry,
            ),
          );
    });
  });

  return true;
};

const wasFinishOptionSelected = lastSection => (_, getState) => {
  const { response } = getState();
  const selectedOptions = [];
  lastSection.questions.forEach(question => {
    selectedOptions.push(...getSelectedOptions(response, question));
  });
  return selectedOptions.length === 1 && selectedOptions[0].finish_survey;
};

const createDuplicateTelemetryData = duration => {
  return { ts: new Date(), duration, back_counter: 0 };
};

export const showCorrectAnswer = actions.showCorrectAnswer;
